import {
  Badge,
  BadgeVariantEnum,
  FontColorEnum,
  FontSizeEnum,
  FontWeightEnum,
  SupportIcon,
  Tooltip,
  Typography,
} from '@acceso/design-system';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import Listbox from '@/components/Listbox';
import { useSupportOptions } from '@/features/programs/hooks/useSupportOptions';
import { TFilterProps } from '@/types';

type MentionFilterSegmentSupportsProps = TFilterProps & {
  value: string[];
  onChange: (next: string[]) => void;
};

export function MentionFilterSegmentSupports({
  isOpen,
  isDark,
  showLabel,
  onToggleFilter,
  onClose,
  className = '',
  value,
  onChange,
}: MentionFilterSegmentSupportsProps) {
  const { t } = useTranslation();
  const { supportOptions } = useSupportOptions();

  const ToolTipWrapper = ({ children }: { children: React.ReactNode }) => {
    if (showLabel) return <>{children}</>;
    return (
      <Tooltip help={t('Soportes')} grow>
        {children}
      </Tooltip>
    );
  };

  return (
    <div className="relative w-full md:w-auto">
      <div
        onClick={onToggleFilter}
        className={clsx(
          'group flex w-full cursor-pointer items-center border-b-[1px] border-stroke-30-1 p-1',
          { 'border-text-medium-20': isOpen && supportOptions.length !== 0 && !isDark },
          { 'hover:border-text-medium-20': !isDark },
          { 'border-text-neutral-10 hover:bg-reverse-opacity-05/10': isDark },
          { 'bg-reverse-opacity-05/10': isOpen && isDark },
          className
        )}
      >
        <ToolTipWrapper>
          <SupportIcon
            className={clsx(
              'mr-1',
              {
                'text-text-medium-10': !isOpen && !isDark,
                'text-accent-active-primary-40': isOpen && !isDark,
              },
              {
                'text-text-neutral-10': isDark,
              },
              {
                'group-hover:text-accent-active-primary-40': !isDark,
              }
            )}
          />
        </ToolTipWrapper>
        {showLabel && (
          <Typography
            fontSize={FontSizeEnum.Sm}
            weight={FontWeightEnum.Medium}
            color={isDark ? FontColorEnum.Neutral : FontColorEnum.Medium}
            className={clsx(
              'select-none pr-size-4',
              {
                'text-text-medium-20': isOpen && !isDark,
              },
              {
                'group-hover:text-text-medium-20': !isDark,
              }
            )}
          >
            {t('Soportes')}
          </Typography>
        )}
        {value.length > 0 && (
          <Badge
            variant={!isDark ? BadgeVariantEnum.DefaultFilled : BadgeVariantEnum.NeutralFilled}
            className="py-3"
          >
            + {value.length}
          </Badge>
        )}
      </div>
      {isOpen && (
        <div className="left-0 top-[120%] z-50 w-full bg-neutral-10 p-3 md:absolute md:min-w-[420px] md:shadow-full-medium">
          <Listbox
            dark={isDark}
            mode="multiple"
            items={supportOptions}
            onChange={onChange}
            identifiers={{
              key: 'value',
              value: 'value',
              label: 'label',
              children: 'options',
            }}
            selected={value}
            onClose={onClose}
          />
        </div>
      )}
    </div>
  );
}
